package com.mgacreative.hotel.feature.reservation.ui.create

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.ui.Alignment
import com.mgacreative.hotel.shared.ui.designsystem.components.HFCard
import com.mgacreative.hotel.shared.ui.designsystem.components.VisualDatePicker
import com.mgacreative.hotel.shared.ui.designsystem.components.toDisplayDate
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.LocalDate
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.plus
import androidx.compose.foundation.clickable
import androidx.compose.material.icons.filled.ArrowDropDown
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateReservationScreen(
    onNavigateBack: () -> Unit,
    viewModel: CreateReservationViewModel = koinViewModel()
) {
    val uiState by viewModel.uiState.collectAsState()

    // Date Picker State
    var showCheckInPicker by remember { mutableStateOf(false) }
    var showCheckOutPicker by remember { mutableStateOf(false) }
    var showBirthDatePicker by remember { mutableStateOf(false) }

    val roomTypes = listOf("Standart Oda", "Deluxe Oda", "Suite", "Aile Odası", "Kral Dairesi")
    var roomTypeExpanded by remember { mutableStateOf(false) }

    LaunchedEffect(uiState.isSuccess) {
        if (uiState.isSuccess) {
            viewModel.resetState()
            onNavigateBack()
        }
    }
    
    // Parse strings to LocalDate for the DatePicker
    val checkInDate = try { LocalDate.parse(uiState.checkInDate) } catch (e: Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
    val checkOutDate = try { LocalDate.parse(uiState.checkOutDate) } catch (e: Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
    val birthDateParsed = try { LocalDate.parse(uiState.birthDate) } catch (e: Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }

    if (showCheckInPicker) {
        VisualDatePicker(
            initialDate = checkInDate,
            onDateSelected = { 
                var newCheckOut = checkOutDate
                if (newCheckOut <= it) {
                    newCheckOut = it.plus(DatePeriod(days = 1))
                }
                viewModel.updateReservationInfo(checkInDate = it.toString(), checkOutDate = newCheckOut.toString())
                showCheckInPicker = false 
            },
            onDismissRequest = { showCheckInPicker = false }
        )
    }

    if (showCheckOutPicker) {
        VisualDatePicker(
            initialDate = checkOutDate,
            onDateSelected = { 
                var newCheckOut = it
                if (it <= checkInDate) {
                    newCheckOut = checkInDate.plus(DatePeriod(days = 1))
                }
                viewModel.updateReservationInfo(checkOutDate = newCheckOut.toString())
                showCheckOutPicker = false 
            },
            onDismissRequest = { showCheckOutPicker = false }
        )
    }

    if (showBirthDatePicker) {
        VisualDatePicker(
            initialDate = birthDateParsed,
            onDateSelected = { 
                viewModel.updateGuestInfo(birthDate = it.toString())
                showBirthDatePicker = false 
            },
            onDismissRequest = { showBirthDatePicker = false }
        )
    }

    Scaffold(
        contentWindowInsets = WindowInsets(0.dp),
        bottomBar = {
            Box(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
                Button(
                    onClick = { viewModel.createReservation() },
                    modifier = Modifier.fillMaxWidth().height(50.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF059669)),
                    enabled = !uiState.isLoading
                ) {
                    if (uiState.isLoading) {
                        CircularProgressIndicator(modifier = Modifier.size(24.dp), color = Color.White)
                    } else {
                        Text("Kaydet", fontSize = 16.sp, fontWeight = FontWeight.Bold, color = Color.White)
                    }
                }
            }
        }
    ) { paddingValues ->
        val scrollState = rememberScrollState()
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(bottom = paddingValues.calculateBottomPadding())
                .background(Color(0xFFF1F5F9))
                .verticalScroll(scrollState)
                .verticalScrollbar(scrollState)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (uiState.errorMessage != null) {
                Card(
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFFEE2E2)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(uiState.errorMessage!!, color = Color(0xFF991B1B), modifier = Modifier.weight(1f))
                        TextButton(onClick = { viewModel.clearError() }) {
                            Text("Kapat", color = Color(0xFF991B1B))
                        }
                    }
                }
            }

            HFCard(modifier = Modifier.fillMaxWidth()) {
                Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text("Müşteri Bilgileri", fontWeight = FontWeight.Bold, color = Color(0xFF1E293B))
                    OutlinedTextField(
                        value = uiState.fullName,
                        onValueChange = { viewModel.updateGuestInfo(fullName = it) },
                        label = { Text("Ad Soyad") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true
                    )
                    OutlinedTextField(
                        value = uiState.phone,
                        onValueChange = { viewModel.updateGuestInfo(phone = it) },
                        label = { Text("Telefon") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true
                    )
                    OutlinedTextField(
                        value = uiState.email,
                        onValueChange = { viewModel.updateGuestInfo(email = it) },
                        label = { Text("E-posta Adresi") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true
                    )
                    OutlinedTextField(
                        value = uiState.idPassportNo,
                        onValueChange = { viewModel.updateGuestInfo(idPassportNo = it) },
                        label = { Text("Kimlik / Pasaport No") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true
                    )
                    OutlinedTextField(
                        value = uiState.country,
                        onValueChange = { viewModel.updateGuestInfo(country = it) },
                        label = { Text("Ülke") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true
                    )
                    Box(modifier = Modifier.fillMaxWidth().clickable { showBirthDatePicker = true }) {
                        OutlinedTextField(
                            value = if (uiState.birthDate.isNotBlank()) uiState.birthDate.toDisplayDate() else "",
                            onValueChange = { },
                            label = { Text("Doğum Tarihi") },
                            enabled = false,
                            colors = OutlinedTextFieldDefaults.colors(
                                disabledTextColor = Color.Black,
                                disabledBorderColor = Color.Gray,
                                disabledLabelColor = Color.Gray
                            ),
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true
                        )
                    }
                }
            }

            HFCard(modifier = Modifier.fillMaxWidth()) {
                Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text("Konaklama Detayları", fontWeight = FontWeight.Bold, color = Color(0xFF1E293B))
                    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        Box(modifier = Modifier.weight(1f).clickable { showCheckInPicker = true }) {
                            OutlinedTextField(
                                value = uiState.checkInDate.toDisplayDate(),
                                onValueChange = { },
                                label = { Text("Giriş") },
                                enabled = false,
                                colors = OutlinedTextFieldDefaults.colors(
                                    disabledTextColor = Color.Black,
                                    disabledBorderColor = Color.Gray,
                                    disabledLabelColor = Color.Gray
                                ),
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                        Box(modifier = Modifier.weight(1f).clickable { showCheckOutPicker = true }) {
                            OutlinedTextField(
                                value = uiState.checkOutDate.toDisplayDate(),
                                onValueChange = { },
                                label = { Text("Çıkış") },
                                enabled = false,
                                colors = OutlinedTextFieldDefaults.colors(
                                    disabledTextColor = Color.Black,
                                    disabledBorderColor = Color.Gray,
                                    disabledLabelColor = Color.Gray
                                ),
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    }
                    Box {
                        OutlinedTextField(
                            value = uiState.selectedRoomType,
                            onValueChange = { },
                            label = { Text("Oda Tipi Seçimi") },
                            modifier = Modifier.fillMaxWidth().clickable { roomTypeExpanded = true },
                            enabled = false,
                            colors = OutlinedTextFieldDefaults.colors(
                                disabledTextColor = Color.Black,
                                disabledBorderColor = Color.Gray,
                                disabledLabelColor = Color.Gray
                            ),
                            trailingIcon = {
                                Icon(Icons.Default.ArrowDropDown, contentDescription = "Seç")
                            }
                        )
                        DropdownMenu(
                            expanded = roomTypeExpanded,
                            onDismissRequest = { roomTypeExpanded = false },
                            modifier = Modifier.fillMaxWidth(0.9f)
                        ) {
                            roomTypes.forEach { type ->
                                DropdownMenuItem(
                                    text = { Text(type) },
                                    onClick = {
                                        viewModel.updateReservationInfo(selectedRoomType = type)
                                        roomTypeExpanded = false
                                    }
                                )
                            }
                        }
                    }
                    Box {
                        var agencyExpanded by remember { mutableStateOf(false) }
                        val agencies by viewModel.agencies.collectAsState()
                        val selectedAgencyName = agencies.find { it.id == uiState.selectedAgencyId }?.name ?: "Walk-in (Acentasız)"
                        
                        OutlinedTextField(
                            value = selectedAgencyName,
                            onValueChange = { },
                            label = { Text("Acenta Seçimi") },
                            modifier = Modifier.fillMaxWidth().clickable { agencyExpanded = true },
                            enabled = false,
                            colors = OutlinedTextFieldDefaults.colors(
                                disabledTextColor = Color.Black,
                                disabledBorderColor = Color.Gray,
                                disabledLabelColor = Color.Gray
                            ),
                            trailingIcon = {
                                Icon(Icons.Default.ArrowDropDown, contentDescription = "Seç")
                            }
                        )
                        DropdownMenu(
                            expanded = agencyExpanded,
                            onDismissRequest = { agencyExpanded = false },
                            modifier = Modifier.fillMaxWidth(0.9f)
                        ) {
                            DropdownMenuItem(
                                text = { Text("Walk-in (Acentasız)") },
                                onClick = {
                                    viewModel.updateReservationInfo(selectedAgencyId = "CLEAR")
                                    agencyExpanded = false
                                }
                            )
                            agencies.forEach { agency ->
                                DropdownMenuItem(
                                    text = { Text(agency.name) },
                                    onClick = {
                                        viewModel.updateReservationInfo(selectedAgencyId = agency.id)
                                        agencyExpanded = false
                                    }
                                )
                            }
                        }
                    }
                    OutlinedTextField(
                        value = uiState.specialNotes,
                        onValueChange = { viewModel.updateReservationInfo(specialNotes = it) },
                        label = { Text("Özel Notlar (Evcil hayvan, bebek yatağı vb.)") },
                        modifier = Modifier.fillMaxWidth(),
                        minLines = 3,
                        maxLines = 5
                    )
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text("Direkt Check-In Yap (Rezervasyonsuz)", fontWeight = FontWeight.SemiBold, color = Color(0xFF1E293B))
                        Switch(
                            checked = uiState.isDirectCheckIn,
                            onCheckedChange = { viewModel.updateReservationInfo(isDirectCheckIn = it) },
                            colors = SwitchDefaults.colors(checkedThumbColor = Color(0xFF059669), checkedTrackColor = Color(0xFF059669).copy(alpha = 0.5f))
                        )
                    }
                }
            }
        }
    }
}
